/**
 * ErrorTrackerV2.js - Enhanced Error Tracking with Per-Section Listeners
 *
 * Each section has its own listener system, allowing ProgressBar to subscribe
 * to specific sections instead of relying on a single global listener.
 */

const IGNORE_KEYS = new Set(["_isValid", "_load"]);

// ===== UTILITY FUNCTIONS =====

function hasAnyError(node) {
  if (!node) return false;
  if (typeof node === "string") return node.trim().length > 0;
  if (Array.isArray(node)) return node.some(hasAnyError);
  if (typeof node === "object") {
    return Object.entries(node).some(([key, value]) => {
      if (IGNORE_KEYS.has(key) || key.startsWith("_")) return false;
      return hasAnyError(value);
    });
  }
  return false;
}

function countErrors(node) {
  let count = 0;
  function traverse(item) {
    if (!item) return;
    if (typeof item === "string") {
      if (item.trim().length > 0) count++;
      return;
    }
    if (Array.isArray(item)) {
      item.forEach(traverse);
      return;
    }
    if (typeof item === "object") {
      Object.entries(item).forEach(([key, value]) => {
        if (IGNORE_KEYS.has(key) || key.startsWith("_")) return;
        traverse(value);
      });
    }
  }
  traverse(node);
  return count;
}

// ===== PER-SECTION ERROR STATE =====

class SectionErrorState {
  constructor(sectionKey) {
    this.sectionKey = sectionKey;
    this.errors = {};
    this.errorCount = 0;
    this.hasErrors = false;
    this.listeners = new Set();
    this.sessionErrors = {}; // { sessionId: { errorCount, hasErrors } }
    this.currentSession = "default";
  }

  // Subscribe to changes for THIS section only
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify only listeners of THIS section
  notifyListeners() {
    const data = {
      sectionKey: this.sectionKey,
      errors: this.errors,
      errorCount: this.errorCount,
      hasErrors: this.hasErrors,
      sessionErrors: this.sessionErrors,
      currentSession: this.currentSession,
    };
    this.listeners.forEach((listener) => listener(data));
  }

  // Update errors for this section
  setErrors(errors = {}) {
    this.errors = errors;
    this.errorCount = countErrors(errors);
    this.hasErrors = hasAnyError(errors);

    // Track per session
    if (!this.sessionErrors[this.currentSession]) {
      this.sessionErrors[this.currentSession] = {};
    }
    this.sessionErrors[this.currentSession].errorCount = this.errorCount;
    this.sessionErrors[this.currentSession].hasErrors = this.hasErrors;

    // Notify this section's listeners
    this.notifyListeners();
  }

  setCurrentSession(sessionId) {
    this.currentSession = sessionId || "default";
  }

  getErrorCount(sessionId = null) {
    const sid = sessionId || this.currentSession;
    return this.sessionErrors[sid]?.errorCount || 0;
  }

  getHasErrors(sessionId = null) {
    const sid = sessionId || this.currentSession;
    return this.sessionErrors[sid]?.hasErrors || false;
  }

  clearSession(sessionId) {
    delete this.sessionErrors[sessionId];
  }
}

// ===== MAIN ERROR TRACKER =====

const errorTrackerV2 = {
  // Per-section error state objects
  sectionStates: {},

  // Global listeners (for backward compatibility)
  globalListeners: new Set(),

  currentSession: "default",

  // Initialize all 11 sections
  initialize() {
    for (let i = 1; i <= 11; i++) {
      const key = `section${i}`;
      if (!this.sectionStates[key]) {
        this.sectionStates[key] = new SectionErrorState(key);
      }
    }
  },

  // ===== PER-SECTION API =====

  /**
   * Subscribe to errors for a SPECIFIC section
   * @param {string} sectionKey - e.g., "section1"
   * @param {Function} callback - Called when this section's errors change
   * @returns {Function} Unsubscribe function
   */
  addSectionListener(sectionKey, callback) {
    this.initialize();
    if (!this.sectionStates[sectionKey]) {
      console.warn(`Section ${sectionKey} not initialized`);
      return () => {};
    }
    return this.sectionStates[sectionKey].addListener(callback);
  },

  /**
   * Get per-section state object
   * ProgressBar can use this to subscribe to specific sections
   */
  getSectionState(sectionKey) {
    this.initialize();
    return this.sectionStates[sectionKey];
  },

  /**
   * Set errors for a section and notify that section's listeners
   */
  setSectionErrors(sectionKey, errors = {}) {
    this.initialize();
    if (!this.sectionStates[sectionKey]) {
      console.warn(`Section ${sectionKey} not initialized`);
      return;
    }

    this.sectionStates[sectionKey].setErrors(errors);
    this.notifyGlobalListeners(); // Also notify global listeners
  },

  /**
   * Merge errors with existing errors (don't replace completely)
   * Used when validating from Firestore to avoid losing UI-tracked errors
   */
  mergeErrors(sectionKey, newErrors = {}) {
    this.initialize();
    if (!this.sectionStates[sectionKey]) {
      console.warn(`Section ${sectionKey} not initialized`);
      return;
    }

    const state = this.sectionStates[sectionKey];
    // Merge new errors with existing ones
    const merged = {
      ...state.errors,
      ...newErrors,
    };
    state.setErrors(merged);
    this.notifyGlobalListeners();
  },

  /**
   * Get all section states as an object
   * Useful for getting overall form state
   */
  getAllSectionStates() {
    this.initialize();
    const states = {};
    Object.entries(this.sectionStates).forEach(([key, state]) => {
      states[key] = {
        errorCount: state.errorCount,
        hasErrors: state.hasErrors,
        errors: state.errors,
      };
    });
    return states;
  },

  /**
   * Get error count for a specific section
   */
  getErrorCount(sectionKey) {
    this.initialize();
    return this.sectionStates[sectionKey]?.getErrorCount() || 0;
  },

  /**
   * Check if a section has errors
   */
  hasErrors(sectionKey) {
    this.initialize();
    if (!sectionKey) {
      // Check all sections
      return Object.values(this.sectionStates).some((state) => state.hasErrors);
    }
    return this.sectionStates[sectionKey]?.hasErrors || false;
  },

  /**
   * Get sections with errors
   */
  getSectionsWithErrors() {
    this.initialize();
    return Object.entries(this.sectionStates)
      .filter(([, state]) => state.hasErrors)
      .map(([key]) => key);
  },

  /**
   * Get total error count across all sections
   */
  getTotalErrorCount() {
    this.initialize();
    return Object.values(this.sectionStates).reduce(
      (sum, state) => sum + state.errorCount,
      0,
    );
  },

  /**
   * Get detailed error summary for all sections
   */
  getErrorSummary() {
    this.initialize();
    return {
      totalErrors: this.getTotalErrorCount(),
      sectionsWithErrors: this.getSectionsWithErrors(),
      errorCounts: Object.entries(this.sectionStates).reduce(
        (acc, [key, state]) => {
          acc[key] = state.errorCount;
          return acc;
        },
        {},
      ),
      sectionsStatus: Object.entries(this.sectionStates).reduce(
        (acc, [key, state]) => {
          acc[key] = {
            hasErrors: state.hasErrors,
            errorCount: state.errorCount,
            errors: state.errors,
          };
          return acc;
        },
        {},
      ),
    };
  },

  // ===== SESSION MANAGEMENT =====

  setCurrentSession(sessionId) {
    this.currentSession = sessionId || "default";
    this.initialize();
    Object.values(this.sectionStates).forEach((state) => {
      state.setCurrentSession(sessionId);
    });
    this.notifyGlobalListeners();
  },

  clearSession(sessionId) {
    this.initialize();
    Object.values(this.sectionStates).forEach((state) => {
      state.clearSession(sessionId);
    });
    this.notifyGlobalListeners();
  },

  // ===== GLOBAL LISTENERS (Backward Compatibility) =====

  addGlobalListener(callback) {
    this.globalListeners.add(callback);
    return () => this.globalListeners.delete(callback);
  },

  notifyGlobalListeners() {
    const summary = this.getErrorSummary();
    this.globalListeners.forEach((listener) => listener(summary));
  },

  // ===== LEGACY API (For backward compatibility) =====

  addListener(callback) {
    return this.addGlobalListener(callback);
  },

  notifyListeners() {
    this.notifyGlobalListeners();
  },

  getErrors(sectionKey) {
    this.initialize();
    return this.sectionStates[sectionKey]?.errors || {};
  },

  ensureSections(sections = []) {
    sections.forEach((key) => {
      if (!this.sectionStates[key]) {
        this.sectionStates[key] = new SectionErrorState(key);
      }
    });
  },

  initializeErrors() {
    this.initialize();
  },

  clearAllErrors() {
    this.initialize();
    Object.values(this.sectionStates).forEach((state) => {
      state.setErrors({});
    });
  },

  getSessionSummary() {
    this.initialize();
    const summary = {};
    Object.entries(this.sectionStates).forEach(([key, state]) => {
      Object.entries(state.sessionErrors).forEach(([sessionId, data]) => {
        if (!summary[sessionId]) {
          summary[sessionId] = {};
        }
        summary[sessionId][key] = data.errorCount;
      });
    });
    return summary;
  },
};

// Initialize on load
errorTrackerV2.initialize();

export default errorTrackerV2;
